import React, { useCallback, useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { Icon } from 'components/Icon';
import { theme, toRem } from 'design';

interface IAccordionProps {
  className?: string;
  dataCy?: string;
  defaultOpenState?: boolean;
  trigger: string | React.ReactNode;
}

const Content = styled(motion.section)`
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: auto;
  border-bottom-right-radius: ${toRem(theme.borderRadius.small)};
  border-bottom-left-radius: ${toRem(theme.borderRadius.small)};
  overflow: hidden;
  transform-origin: left top;
`;

const AccordionLayout = styled.div`
  display: flex;
  flex-flow: column;
`;

const DropdownIcon = styled(Icon)`
  &.open {
    transform: rotate(180deg);
  }
`;

const Trigger = styled.button`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: auto;
  padding: ${toRem(theme.space.xs)} 0;
  background: none;
  border: none;
  outline: none;

  &:hover {
    cursor: pointer;
  }

  &:focus-visible {
    outline: 1px dashed ${theme.color.neutral[800]};
  }
`;

const TriggerWrapper = styled.div``;

const Accordion: React.FC<IAccordionProps> = ({ children, className = '', dataCy = 'accordion', defaultOpenState = false, trigger }) => {
  const [open, setOpen] = useState(defaultOpenState);

  const onTriggerClick = useCallback(() => {
    setOpen(!open);
  }, [open]);

  return (
    <AccordionLayout className={className} data-cy={dataCy}>
      <Trigger onClick={onTriggerClick}>
        <TriggerWrapper>{trigger}</TriggerWrapper>
        <DropdownIcon className={open ? 'open' : ''} icon="Dropdown" fill={theme.color.neutral[500]} />
      </Trigger>

      <Content initial={open} animate={{ height: open ? 'auto' : 0 }} transition={theme.transition.spring.accordion} data-cy={`${dataCy}-content`}>
        {children}
      </Content>
    </AccordionLayout>
  );
};

export default Accordion;
